import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/apiClient";
import RadialGauge from "@/components/RadialGauge";
import ProfileBuilder from "@/components/ProfileBuilder";
import { toast } from "sonner";
import { Building2, AlertTriangle, ShieldCheck, FileText, Sparkles, Loader2, TrendingUp, Download, LineChart as LineChartIcon, Plus, Trash2, Search, X, Eye, EyeOff, CheckCircle2, Zap, ArrowRight } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function Tracks() {
  const { user, refreshUser } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [active, setActive] = useState(0);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiAdvice, setAiAdvice] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [cc, setCc] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [togglingDisc, setTogglingDisc] = useState(false);

  const loadTracks = async () => {
    const { data } = await api.get("/tracks");
    setTracks(data.tracks);
    setLoading(false);
    return data.tracks;
  };

  const loadCC = async () => {
    try { const { data } = await api.get("/command-center"); setCc(data); } catch { /* ignore */ }
  };

  const loadDetail = async (careerId) => {
    const { data } = await api.get(`/careers/${careerId}`);
    setDetail(data);
  };

  const loadHistory = async (trackId) => {
    const { data } = await api.get(`/tracks/${trackId}/history`);
    setHistory(data.history.map((h) => ({ date: h.date.slice(5), score: h.score })));
  };

  useEffect(() => { loadTracks(); loadCC(); }, []);
  useEffect(() => {
    if (tracks[active]) { loadDetail(tracks[active].career_id); loadHistory(tracks[active].track_id); setAiAdvice(""); }
  }, [active, tracks.length]);

  const reloadTracks = async () => {
    const t = await loadTracks();
    if (t[active]) { loadDetail(t[active].career_id); loadHistory(t[active].track_id); }
    await refreshUser();
  };

  const getAdvice = async () => {
    setAiLoading(true);
    setAiAdvice("");
    try {
      const { data } = await api.get(`/tracks/${tracks[active].track_id}/ai-advice`);
      setAiAdvice(data.advice);
    } catch (e) { toast.error(apiErr(e)); } finally { setAiLoading(false); }
  };

  const addTrack = async (role) => {
    try {
      await api.post("/tracks", { career_id: role.career_id, label: role.role });
      toast.success(`Added "${role.role}" to your dream jobs`);
      setShowAdd(false);
      await reloadTracks();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const deleteTrack = async (tr) => {
    if (!window.confirm(`Remove "${tr.career_name}" from your dream jobs? This cannot be undone.`)) return;
    try {
      await api.delete(`/tracks/${tr.track_id}`);
      toast.success("Track removed");
      setActive(0);
      await reloadTracks();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggleDiscoverable = async (tr) => {
    setTogglingDisc(true);
    try {
      const { data } = await api.patch(`/tracks/${tr.track_id}/discoverable`, { discoverable: !tr.discoverable });
      toast.success(data.discoverable ? `Recruiters can now find you for "${tr.career_name}"` : `Hidden "${tr.career_name}" from recruiters`);
      await loadTracks();
    } catch (e) { toast.error(apiErr(e)); } finally { setTogglingDisc(false); }
  };

  const completeAction = async () => {
    if (!cc?.micro_action) return;
    setCompleting(true);
    try {
      const { data } = await api.post(`/actions/${cc.micro_action.id}/complete`);
      toast.success(`+${data.xp_earned} XP earned! Streak: ${data.current_streak} 🔥`);
      await refreshUser();
      await reloadTracks();
      await loadCC();
    } catch (e) { toast.error(apiErr(e)); } finally { setCompleting(false); }
  };

  if (loading) return <div className="h-96 flex items-center justify-center"><Loader2 className="animate-spin text-[#6366F1]" /></div>;

  if (tracks.length === 0)
    return (
      <div className="max-w-lg">
        <h1 className="font-display font-extrabold text-3xl tracking-tight text-white">Career Track Hub</h1>
        <div className="bg-white rounded-2xl p-8 border border-dashed border-slate-300 mt-6 text-center">
          <p className="text-slate-500">You haven't selected any career tracks yet.</p>
          <button data-testid="empty-add-track-btn" onClick={() => setShowAdd(true)} className="inline-block mt-3 text-[#6366F1] font-semibold hover:underline">Add a dream job →</button>
        </div>
        {showAdd && <AddTrackModal onPick={addTrack} onClose={() => setShowAdd(false)} />}
      </div>
    );

  const t = tracks[active];
  const r = t.readiness;

  return (
    <div className="max-w-6xl">
      <p className="eyebrow text-slate-400">CAREER TRACK HUB</p>
      <h1 className="font-display font-extrabold text-3xl tracking-tight text-white mt-1 mb-6">Your readiness, by dream career</h1>

      {cc?.micro_action && (
        <div className="bg-white rounded-2xl p-6 border-2 border-[#6366F1] shadow-[0_0_28px_rgba(99,102,241,0.18)] mb-6" data-testid="micro-action-card">
          <div className="flex items-center justify-between mb-3">
            <span className="inline-flex items-center gap-1.5 eyebrow text-[#6366F1]"><Sparkles size={14} /> TODAY'S HIGH-IMPACT MOVE</span>
            <span className="text-xs font-mono px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">{cc.micro_action.Action_Type}</span>
          </div>
          <h2 className="font-display font-bold text-xl text-slate-900 tracking-tight leading-snug">{cc.micro_action.Title}</h2>
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#22C55E]/10 border border-[#22C55E]/30 text-[#16A34A] font-mono text-sm font-bold">
              <TrendingUp size={15} /> +{cc.micro_action.impact_pct}% {cc.micro_action.career_name}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#6366F1]/10 border border-[#6366F1]/30 text-[#4F46E5] font-mono text-sm font-bold">
              <Zap size={15} /> +{cc.micro_action.XP_Reward} XP
            </span>
            <button data-testid="complete-action-btn" onClick={completeAction} disabled={completing}
              className="ml-auto inline-flex items-center gap-2 bg-[#6366F1] hover:bg-[#4F46E5] active:scale-[0.98] text-white font-semibold px-5 py-2.5 rounded-lg transition-colors disabled:opacity-60">
              {completing ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={18} />} Mark complete
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {tracks.map((tr, i) => (
          <button key={tr.track_id} data-testid={`track-tab-${tr.career_id}`} onClick={() => setActive(i)}
            className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors border ${
              active === i ? "bg-[#6366F1] text-white border-[#6366F1]" : "bg-slate-800/60 text-slate-300 border-slate-700 hover:bg-slate-800"
            }`}>
            {tr.career_name}
            {tr.discoverable && <Eye size={13} className="inline ml-1.5 -mt-0.5 opacity-80" />}
            <span className="ml-2 font-mono text-xs opacity-80">{tr.readiness.score}%</span>
          </button>
        ))}
        {tracks.length < 5 && (
          <button data-testid="add-track-btn" onClick={() => setShowAdd(true)}
            className="px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors border border-dashed border-slate-600 text-slate-300 hover:border-[#6366F1] hover:text-white inline-flex items-center gap-1.5">
            <Plus size={15} /> Add dream job
          </button>
        )}
      </div>

      {/* Top row: gauge + meta */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-2xl p-6 border border-slate-200 flex flex-col items-center justify-center" data-testid="readiness-gauge-card">
          <RadialGauge value={r.score} size={190} stroke={13} />
          <p className="text-sm text-slate-500 mt-4 text-center font-medium">{r.skills_production} of {r.skills_total} core skills production-ready</p>
        </div>
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-slate-200">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-display font-bold text-xl text-slate-900 tracking-tight">{t.career_name}</h2>
              <p className="text-sm text-slate-500 mt-1">{t.career_family}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">Priority {t.priority_rank}</span>
              <button data-testid="delete-track-btn" onClick={() => deleteTrack(t)} title="Remove this dream job"
                className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-5">
            <div className="p-4 rounded-xl bg-green-50 border border-green-200">
              <div className="flex items-center gap-2 text-green-700 eyebrow"><ShieldCheck size={14} /> AUTOMATION RISK</div>
              <p className="text-sm font-semibold text-slate-800 mt-1">{t.automation_risk || "—"}</p>
            </div>
            <div className="p-4 rounded-xl bg-indigo-50 border border-indigo-200">
              <div className="flex items-center gap-2 text-[#4F46E5] eyebrow"><TrendingUp size={14} /> FUTURE GROWTH</div>
              <p className="text-sm font-semibold text-slate-800 mt-1">{t.future_growth || "—"}</p>
            </div>
          </div>
          <button data-testid="ai-advice-btn" onClick={getAdvice} disabled={aiLoading}
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#6366F1] hover:text-[#4F46E5] transition-colors disabled:opacity-60">
            {aiLoading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Get AI coaching for this track
          </button>
          {aiAdvice && (
            <div data-testid="ai-advice-box" className="mt-3 p-4 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
              {aiAdvice}
            </div>
          )}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2 text-sm">
              {t.discoverable ? <Eye size={15} className="text-[#22C55E]" /> : <EyeOff size={15} className="text-slate-400" />}
              <span className="text-slate-600">{t.discoverable ? "Recruiters can find you for this role" : "Private — recruiters can't see this role"}</span>
            </div>
            <button data-testid="discoverable-toggle" onClick={() => toggleDiscoverable(t)} disabled={togglingDisc}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-60 ${
                t.discoverable ? "bg-[#22C55E]/10 text-[#16A34A] border border-[#22C55E]/30 hover:bg-[#22C55E]/20" : "bg-[#6366F1] text-white hover:bg-[#4F46E5]"
              }`}>
              {t.discoverable ? <><Eye size={13} /> Visible — tap to hide</> : <><EyeOff size={13} /> Make me discoverable</>}
            </button>
          </div>
        </div>
      </div>

      {/* Progress over time */}
      <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-6" data-testid="progress-chart">
        <div className="flex items-center gap-2 text-slate-700 eyebrow mb-4"><LineChartIcon size={14} className="text-[#6366F1]" /> READINESS OVER TIME</div>
        {history.length <= 1 ? (
          <div className="h-[180px] flex flex-col items-center justify-center text-center">
            <p className="text-sm text-slate-500">Your progress chart builds as your readiness changes.</p>
            <p className="text-xs text-slate-400 mt-1">Log skills or complete actions to plot your climb.</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={history} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94A3B8" }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#94A3B8" }} tickLine={false} axisLine={false} unit="%" />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} formatter={(v) => [`${v}%`, "Readiness"]} />
              <Line type="monotone" dataKey="score" stroke="#6366F1" strokeWidth={3} dot={{ r: 4, fill: "#6366F1" }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Build your profile — 6-category builder scoped to this track */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ProfileBuilder trackId={t.track_id} careerName={t.career_name}
            onReadiness={(r) => { if (Math.abs((r?.score ?? 0) - t.readiness.score) > 0.01) reloadTracks(); }} />
        </div>

        {/* Companies + gaps + resume */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-6 border border-slate-200" data-testid="skill-gaps">
            <div className="flex items-center gap-2 text-amber-600 eyebrow mb-3"><AlertTriangle size={14} /> KEY SKILL GAPS</div>
            {r.missing_essential.length === 0 ? (
              <p className="text-sm text-green-600 font-medium">No essential gaps. Strong foundation!</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {r.missing_essential.slice(0, 8).map((s) => (
                  <span key={s.skill_id} className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-red-50 text-red-600 border border-red-200">{s.skill_name}</span>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl p-6 border border-slate-200" data-testid="hiring-companies">
            <div className="flex items-center gap-2 text-[#4F46E5] eyebrow mb-3"><Building2 size={14} /> TOP HIRING COMPANIES</div>
            <div className="space-y-2 max-h-52 overflow-y-auto">
              {(detail?.companies || []).slice(0, 8).map((c, i) => (
                <div key={i} className="flex items-center justify-between gap-2 text-sm">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{c.company_name}</p>
                    <p className="text-[11px] text-slate-400 truncate">{c.salary_min}</p>
                  </div>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-green-50 text-green-700 whitespace-nowrap">{c.hiring_intensity || "Hiring"}</span>
                </div>
              ))}
              {(!detail?.companies || detail.companies.length === 0) && <p className="text-sm text-slate-400">No mapped companies.</p>}
            </div>
          </div>

          <ResumePreview user={user} career={t} readiness={r} />
        </div>
      </div>

      {showAdd && <AddTrackModal onPick={addTrack} onClose={() => setShowAdd(false)} />}
    </div>
  );
}

function AddTrackModal({ onPick, onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get("/careers/roles", { params: { search: q, limit: 10 } });
      setResults(data.roles);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { const t = setTimeout(() => search(query), 250); return () => clearTimeout(t); }, [query, search]);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4 py-8" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="add-track-modal">
        <div className="flex items-start justify-between mb-1">
          <div>
            <h2 className="font-display font-extrabold text-xl tracking-tight text-slate-900">Add a dream job</h2>
            <p className="text-sm text-slate-500 mt-0.5">Search a role to build a new readiness track.</p>
          </div>
          <button data-testid="add-track-close" onClick={onClose}><X size={20} className="text-slate-400 hover:text-slate-700" /></button>
        </div>
        <div className="relative mt-4">
          <Search size={18} className="absolute left-3 top-3 text-slate-400" />
          <input data-testid="add-track-search" autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search job titles..."
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 text-slate-900 focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/20 outline-none transition-colors" />
        </div>
        <div className="mt-3 max-h-72 overflow-y-auto space-y-1">
          {loading && <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-[#6366F1]" size={18} /></div>}
          {!loading && results.map((r, i) => (
            <button key={r.id} data-testid={`add-role-option-${i}`} onClick={() => onPick(r)}
              className="w-full text-left px-4 py-3 rounded-lg border border-slate-200 hover:border-[#6366F1] hover:bg-[#6366F1]/5 transition-colors">
              <p className="font-semibold text-slate-900 text-sm">{r.role}</p>
              <p className="text-xs text-slate-500">{r.field_label || r.field_name}</p>
            </button>
          ))}
          {!loading && results.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No roles found.</p>}
        </div>
      </div>
    </div>
  );
}

function ResumePreview({ user, career, readiness }) {
  const [events, setEvents] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [theme, setTheme] = useState("indigo");
  useEffect(() => { api.get("/timeline").then(({ data }) => setEvents(data.events)); }, []);

  const THEMES = [
    { id: "indigo", color: "#6366F1", label: "Indigo" },
    { id: "emerald", color: "#22C55E", label: "Emerald" },
    { id: "classic", color: "#0F172A", label: "Classic" },
  ];

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/tracks/${career.track_id}/resume.pdf`, { params: { theme }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(user?.full_name || "resume").replace(/\s+/g, "_")}_resume.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Resume downloaded");
    } catch (e) { toast.error("Could not generate PDF"); } finally { setDownloading(false); }
  };

  return (
    <div className="bg-white rounded-2xl p-6 border border-slate-200" data-testid="resume-preview">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-slate-700 eyebrow"><FileText size={14} /> AUTO-GENERATED RESUME</div>
        <button data-testid="download-resume-btn" onClick={downloadPdf} disabled={downloading}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#6366F1] hover:text-[#4F46E5] transition-colors disabled:opacity-60">
          {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} PDF
        </button>
      </div>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[11px] text-slate-400">Theme:</span>
        {THEMES.map((th) => (
          <button key={th.id} data-testid={`resume-theme-${th.id}`} onClick={() => setTheme(th.id)} title={th.label}
            className={`h-6 w-6 rounded-full border-2 transition-transform hover:scale-110 ${theme === th.id ? "ring-2 ring-offset-1 ring-slate-300 scale-110" : "border-white"}`}
            style={{ backgroundColor: th.color, borderColor: theme === th.id ? th.color : "#fff" }} />
        ))}
        <span className="text-[11px] font-medium text-slate-500 ml-1 capitalize">{theme}</span>
      </div>
      <div className="border border-slate-200 rounded-lg p-4 text-slate-800">
        <p className="font-display font-bold text-lg">{user?.full_name}</p>
        <p className="text-xs text-slate-500">{user?.email} · Target: {career.career_name}</p>
        <p className="text-[11px] font-mono text-[#6366F1] mt-1">{readiness.score}% Career Readiness</p>
        <hr className="my-3 border-slate-200" />
        <p className="eyebrow text-slate-400 mb-1">EDUCATION</p>
        {(user?.education || []).filter((e) => e.other || e.degree).length > 0 ? (
          <div className="space-y-1.5" data-testid="resume-education">
            {(user.education).filter((e) => e.other || e.degree).map((e, i) => {
              const name = e.other || e.degree;
              const when = e.status === "pursuing"
                ? (e.current_semester ? `Pursuing · Sem ${e.current_semester}` : "Pursuing")
                : (e.completion_year ? `Completed ${e.completion_year}` : "Completed");
              const meta = [e.institution, when, e.overall_cgpa ? `CGPA ${e.overall_cgpa}` : ""].filter(Boolean).join(" · ");
              const trend = (e.semesters || []).filter((s) => String(s.grade || "").trim());
              return (
                <div key={e.id || i}>
                  <p className="text-sm font-medium">{name} {meta && <span className="text-xs text-slate-400 font-normal">· {meta}</span>}</p>
                  {trend.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {trend.map((s, j) => (
                        <span key={s.id || j} className="text-[10px] font-mono px-1 py-0.5 rounded bg-slate-100 text-slate-500">S{s.sem}: {s.grade}</span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm">{user?.graduation_degree || "—"} {user?.cgpa ? `· CGPA ${user.cgpa}` : ""}</p>
        )}
        <p className="text-xs text-slate-500 mt-1">Class 12: {user?.class_12_score || "—"} · Class 10: {user?.class_10_score || "—"}</p>
        <p className="eyebrow text-slate-400 mt-3 mb-1">VERIFIED SKILLS</p>
        <div className="flex flex-wrap gap-1">
          {readiness.skills.filter((s) => s.user_level !== "none").slice(0, 10).map((s) => (
            <span key={s.skill_id} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{s.skill_name}</span>
          ))}
          {readiness.skills.filter((s) => s.user_level !== "none").length === 0 && <span className="text-xs text-slate-400">Log skills to populate.</span>}
        </div>
        <p className="eyebrow text-slate-400 mt-3 mb-1">ACHIEVEMENTS</p>
        {events.filter((e) => e.Activity_Type !== "Skill Acquired").slice(0, 5).map((e) => (
          <p key={e.id} className="text-xs text-slate-600">• {e.Title}</p>
        ))}
        {events.filter((e) => e.Activity_Type !== "Skill Acquired").length === 0 && <span className="text-xs text-slate-400">No achievements yet.</span>}
      </div>
    </div>
  );
}
